import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const Healthcare = () => {
  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        background: `
          radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)
        `
      }}
    >
      <main className="max-w-7xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-6 mt-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="border border-[hsl(var(--line))] rounded-[14px] p-4 sm:p-6 md:p-12" style={{ boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)" }}>
          <h1 className="font-orbitron font-bold text-3xl sm:text-4xl md:text-5xl mb-6 leading-tight">
            Healthcare <span className="text-primary">Automation</span>
          </h1>

          <section className="mb-10 md:mb-12">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl mb-4">The Challenge</h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-4 leading-relaxed">
              Healthcare facilities face critical staffing shortages, infection control demands, and the need to keep clinical staff focused on patient care rather than logistics and cleaning.
            </p>
          </section>

          <section className="mb-10 md:mb-12">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl mb-4">The Solution</h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed">
              Our Healthcare Bundle includes CC1 Pro for disinfection-grade cleaning, PUDU T600 for supply delivery, and HIPAA-compliant AI Voice for appointment routing and patient inquiries.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-[hsl(var(--line))] glass-effect p-6 rounded-xl">
                <h3 className="font-orbitron font-bold text-lg mb-2">CC1 Pro Cleaning</h3>
                <p className="text-sm text-muted-foreground">Hospital-grade floor cleaning and disinfection for patient areas</p>
                <p className="text-primary font-bold mt-2 text-sm">Infection control certified</p>
              </div>
              
              <div className="border border-[hsl(var(--line))] glass-effect p-6 rounded-xl">
                <h3 className="font-orbitron font-bold text-lg mb-2">PUDU T600 Delivery</h3>
                <p className="text-sm text-muted-foreground">Heavy-payload delivery robot with 600kg capacity for supplies, linens, and medications</p>
                <p className="text-primary font-bold mt-2 text-sm">Multi-floor • 12hr battery • Touch screen</p>
              </div>
              
              <div className="border border-[hsl(var(--line))] glass-effect p-6 rounded-xl">
                <h3 className="font-orbitron font-bold text-lg mb-2">AI Voice (HIPAA)</h3>
                <p className="text-sm text-muted-foreground">24/7 appointment scheduling, prescription refills, and patient routing</p>
                <p className="text-primary font-bold mt-2 text-sm">Secure & compliant</p>
              </div>
            </div>
          </section>

          <section className="mb-10 md:mb-12">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl mb-4">Results You Can Expect</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">✓</span>
                <span className="text-muted-foreground">Free nurses and staff from non-clinical tasks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">✓</span>
                <span className="text-muted-foreground">Reduce hospital-acquired infections with consistent cleaning</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">✓</span>
                <span className="text-muted-foreground">Save $5,000–$10,000/month in labor costs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">✓</span>
                <span className="text-muted-foreground">Improve patient satisfaction with faster response times</span>
              </li>
            </ul>
          </section>

          <section className="text-center">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl mb-4 leading-tight">Improve Patient Care with Automation</h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
              Let your staff focus on patients, not logistics and cleaning.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl"
            >
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Schedule Healthcare Demo
              </a>
            </Button>
          </section>
        </div>
      </main>
      <section className="mx-auto w-full max-w-7xl px-[clamp(1rem,5vw,1.5rem)] pb-16">
        <a href="/robots/humanoid/healthcare" className="block rounded-2xl border border-[hsl(var(--line))] glass-effect p-6 hover:border-primary/50 transition-colors">
          <p className="text-xs uppercase tracking-wide text-accent">Humanoid Division</p>
          <p className="mt-2 font-orbitron text-lg font-bold">Humanoid Robotics for Healthcare &amp; Senior Care</p>
          <p className="mt-2 text-sm text-muted-foreground">Connected care technology, service humanoids, AI automation and Robotics-as-a-Service for hospitals and senior-living communities.</p>
        </a>
      </section>
    </div>
  );
};

export default Healthcare;
